import { sendGTMEvent } from '@next/third-parties/google'

export const gtmConfig = {
  gtmId: process.env.GTM_ID || 'GTM-XXXXXX',
  dataLayerName: 'dataLayer',
  dataLayer: {
    vertical: 'LP Dona',
  },
}

/**
 * Invia un evento di pageview a GTM
 * @param {Object} pageData - Dati della pagina
 * @param {string} pageData.pageUrl - URL completo della pagina
 * @param {string} pageData.pageTitle - Titolo della pagina
 * @param {string} pageData.content_group - Sezione di riferimento della pagina
 * @param {string} [pageData.vertical] - Vertical (default: 'LP Dona')
 */
export function trackPageView({
  pageUrl,
  pageTitle,
  content_group,
  vertical = 'LP Dona',
}) {
  try {
    if (!pageUrl || !pageTitle || !content_group) {
      throw new Error(
        'PageUrl, pageTitle, and content_group are required for pageview tracking'
      )
    }

    const eventData = {
      event: 'virtualPageview',
      pageUrl,
      pageTitle,
      content_group,
      vertical,
    }

    console.info('📄[GTM PAGEVIEW]:', eventData)

    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM PAGEVIEW ERROR]:', error)
    throw error
  }
}

/**
 * Helper per ottenere automaticamente i dati della pagina corrente
 * @param {string} content_group - Sezione di riferimento della pagina
 * @param {string} [vertical] - Vertical (default: 'LP Dona')
 */
export function trackCurrentPage(content_group, vertical = 'LP Dona') {
  if (typeof window === 'undefined') {
    console.warn('trackCurrentPage can only be called on the client side')
    return
  }

  // Se content_group e' gia' stato tracciato, non inviare di nuovo
  if (
    window.dataLayer &&
    window.dataLayer.some((event) => event.content_group === content_group)
  ) {
    console.info(
      '📄[GTM PAGEVIEW SKIPPED]: Already tracked for content_group:',
      content_group
    )
    return
  }

  trackPageView({
    pageUrl: window.location.href,
    pageTitle: document.title,
    content_group,
    vertical,
  })
}

/**
 * Invia un evento di click CTA a GTM
 * @param {string} cta_label - Titolo/label del bottone cliccato
 */
export function trackCTAClick(cta_label) {
  try {
    if (!cta_label) {
      throw new Error('cta_label is required for CTA click tracking')
    }

    const eventData = {
      event: 'CTAClick',
      eventName: 'dona_cta_click',
      cta_label,
    }

    console.info('🎯[GTM CTA CLICK]:', eventData)

    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM CTA CLICK ERROR]:', error)
    throw error
  }
}

/**
 * Invia evento begin_checkout per il primo step della donazione
 * @param {Object} donationData - Dati della donazione
 * @param {string} donationData.item_name - Tipo di donazione
 * @param {string} donationData.item_id - Tipo di donazione
 * @param {number} donationData.price - Valore donazione
 * @param {string} donationData.item_category - Tipologia donazione
 * @param {string} donationData.item_category2 - Tipologia sub donazione
 * @param {string} donationData.item_variant - Tipologia donazione
 * @param {number} donationData.index - Posizione donazione scelta
 */
export function trackBeginCheckout(donationData) {
  try {
    const {
      item_name,
      item_id,
      price,
      item_category,
      item_category2,
      item_variant,
      index = 1,
    } = donationData

    if (!item_name || !item_id || !price) {
      throw new Error(
        'item_name, item_id, and price are required for begin_checkout tracking'
      )
    }

    // Clear ecommerce data first
    sendGTMEvent({ ecommerce: null })

    // Send begin_checkout event
    const eventData = {
      event: 'begin_checkout',
      eventName: 'begin_checkout',
      ecommerce: {
        items: [
          {
            item_name,
            item_id,
            price: price.toString(),
            item_brand: 'LP Dona',
            item_category: item_category || '',
            item_category2: item_category2 || '',
            item_category3: 'AIRC > Lp Dona',
            item_category4: '',
            item_category5: '',
            item_variant: item_variant || '',
            item_list_name: 'Donazioni',
            item_list_id: 'Donazioni',
            index,
            quantity: '1',
            currency: 'EUR',
          },
        ],
      },
    }

    console.info('🛒[GTM BEGIN CHECKOUT]:', eventData)
    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM BEGIN CHECKOUT ERROR]:', error)
    throw error
  }
}

/**
 * Invia evento add_shipping_info per lo step 3 (dati donatore)
 * @param {Object} donationData - Dati della donazione
 * @param {string} donationData.item_name - Tipo di donazione
 * @param {string} donationData.item_id - Tipo di donazione
 * @param {number} donationData.price - Valore donazione
 * @param {string} donationData.item_category - Tipologia donazione
 * @param {string} donationData.item_category2 - Tipologia sub donazione
 * @param {string} donationData.item_variant - Tipologia donazione
 * @param {number} donationData.index - Posizione donazione scelta
 */
export function trackAddShippingInfo(donationData) {
  try {
    const {
      item_name,
      item_id,
      price,
      item_category,
      item_category2,
      item_variant,
      index = 1,
    } = donationData

    if (!item_name || !item_id || !price) {
      throw new Error(
        'item_name, item_id, and price are required for add_shipping_info tracking'
      )
    }

    // Clear ecommerce data first
    sendGTMEvent({ ecommerce: null })

    // Send add_shipping_info event
    const eventData = {
      event: 'add_shipping_info',
      eventName: 'add_shipping_info',
      ecommerce: {
        items: [
          {
            item_name,
            item_id,
            price: price.toString(),
            item_brand: 'LP Dona',
            item_category: item_category || '',
            item_category2: item_category2 || '',
            item_category3: 'AIRC > LP Dona',
            item_category4: '',
            item_category5: '',
            item_variant: item_variant || '',
            item_list_name: 'Donazioni',
            item_list_id: 'Donazioni',
            index,
            quantity: '1',
            currency: 'EUR',
          },
        ],
      },
    }

    console.info('🚚[GTM ADD SHIPPING INFO]:', eventData)
    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM ADD SHIPPING INFO ERROR]:', error)
    throw error
  }
}

/**
 * Invia evento "Da dove doni" per lo step 4
 */
export function trackDaDoveDoni() {
  try {
    const eventData = {
      event: 'daDoveDoni',
      eventName: 'funnel_dadovedoni',
    }

    console.info('📍[GTM DA DOVE DONI]:', eventData)
    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM DA DOVE DONI ERROR]:', error)
    throw error
  }
}

/**
 * Invia evento add_payment_info per gli step 8/9 (metodo di pagamento)
 * @param {Object} donationData - Dati della donazione
 * @param {string} donationData.item_name - Tipo di donazione
 * @param {string} donationData.item_id - Tipo di donazione
 * @param {number} donationData.price - Valore donazione
 * @param {string} donationData.item_category - Tipologia donazione
 * @param {string} donationData.item_category2 - Tipologia sub donazione
 * @param {string} donationData.item_variant - Tipologia donazione
 * @param {number} donationData.index - Posizione donazione scelta
 */
export function trackAddPaymentInfo(donationData) {
  try {
    const {
      item_name,
      item_id,
      price,
      item_category,
      item_category2,
      item_variant,
      index = 1,
    } = donationData

    if (!item_name || !item_id || !price) {
      throw new Error(
        'item_name, item_id, and price are required for add_payment_info tracking'
      )
    }

    // Clear ecommerce data first
    sendGTMEvent({ ecommerce: null })

    // Send add_payment_info event
    const eventData = {
      event: 'add_payment_info',
      eventName: 'add_payment_info',
      ecommerce: {
        items: [
          {
            item_name,
            item_id,
            price: price.toString(),
            item_brand: 'LP Dona',
            item_category: item_category || '',
            item_category2: item_category2 || '',
            item_category3: 'AIRC > LP Dona',
            item_category4: '',
            item_category5: '',
            item_variant: item_variant || '',
            item_list_name: 'Donazioni',
            item_list_id: 'Donazioni',
            index,
            quantity: '1',
            currency: 'EUR',
          },
        ],
      },
    }

    console.info('💳[GTM ADD PAYMENT INFO]:', eventData)
    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM ADD PAYMENT INFO ERROR]:', error)
    throw error
  }
}

/**
 * Invia evento purchase quando la donazione è completata con successo
 * @param {Object} purchaseData - Dati della transazione
 * @param {string} purchaseData.payment_type - Tipo di pagamento
 * @param {string} purchaseData.transaction_id - ID transazione
 * @param {number} purchaseData.value - Valore totale
 * @param {string} purchaseData.item_name - Tipo di donazione
 * @param {string} purchaseData.item_id - Tipo di donazione
 * @param {number} purchaseData.price - Valore donazione
 * @param {string} purchaseData.item_category - Tipologia donazione
 * @param {string} purchaseData.item_category2 - Tipologia sub donazione
 * @param {string} purchaseData.item_variant - Tipologia donazione
 * @param {number} purchaseData.index - Posizione donazione scelta
 */
export function trackPurchase(purchaseData) {
  try {
    const {
      payment_type,
      transaction_id,
      value,
      item_name,
      item_id,
      price,
      item_category,
      item_category2,
      item_variant,
      index = 1,
    } = purchaseData

    if (
      !payment_type ||
      !transaction_id ||
      !value ||
      !item_name ||
      !item_id ||
      !price
    ) {
      throw new Error(
        'payment_type, transaction_id, value, item_name, item_id, and price are required for purchase tracking'
      )
    }

    // Clear ecommerce data first
    sendGTMEvent({ ecommerce: null })

    // Send purchase event
    const eventData = {
      event: 'purchase',
      eventName: 'purchase',
      payment_type,
      ecommerce: {
        transaction_id,
        affiliation: 'LP Dona',
        value: parseFloat(value),
        tax: 0.0,
        shipping: 0.0,
        currency: 'EUR',
        items: [
          {
            item_name,
            item_id,
            price: price.toString(),
            item_brand: 'LP Dona',
            item_category: item_category || '',
            item_category2: item_category2 || '',
            item_category3: 'AIRC > LP Dona',
            item_category4: '',
            item_category5: '',
            item_variant: item_variant || '',
            item_list_name: 'Donazioni',
            item_list_id: 'Donazioni',
            index,
            quantity: '1',
            currency: 'EUR',
          },
        ],
      },
    }

    console.info('🎉[GTM PURCHASE]:', eventData)
    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM PURCHASE ERROR]:', error)
    throw error
  }
}
